import { gql } from '@/utilities/gql';
import type { ResultOf } from '@/utilities/gql';
import { gqlClient } from '@/utilities/graphQLClient';
import { useQuery, type QueryFunctionContext } from '@tanstack/react-query';
import type { PreferredMultiple } from './selector';
import type { GraphQLError } from 'graphql';
import { PEERS_LIMIT } from '../../constants';

export function isPreferredMultiple(
  multiple = 'PE'
): multiple is PreferredMultiple {
  return ['PS', 'PE', 'PB'].includes(multiple?.toUpperCase());
}
export function getHistogramQueryKey(
  canonicalURL?: string,
  multiple?: PreferredMultiple
): [string, string, string | undefined] {
  return ['COMPANY_INDUSTRY_HISTOGRAM', canonicalURL || '', multiple];
}

export function getPeersQueryKey(
  canonicalURL?: string,
  limit?: number,
  customPeers?: string[]
): [string, string, number, string[]] {
  return ['COMPANY_PEERS', canonicalURL || '', limit || 4, customPeers || []];
}

const COMPANY_FIELDS_FRAGMENT = gql(`
  fragment CompanyFields on Company @_unmask {
    id
    name
    tickerSymbol
    uniqueSymbol
    canonicalURL
    exchangeCountryISO
    listingCurrencyISO
    isPrimaryListing
    primaryIndustry {
      id
    }
    analysisValue {
      priceTargetAnalystCount
      marketCap
      marketCapUSD
      pe
      pb
      priceToSales
      return1yAbs
      return7d
      priceTarget
      lastSharePrice
    }
    analysisFuture {
      netIncome1Y
      revenue1Y
      netIncomeGrowth1Y
      revenueGrowth1Y
      forwardPe1Y
      forwardPriceToSales1Y
      netIncomeGrowthAnnual
      revenueGrowthAnnual
    }
    country {
      iso2
      region {
        id
        name
      }
    }
    score {
      value
      future
      past
      health
      dividend
    }
  }
`);

const QUERY = gql(
  `
    query getCompanyPeers(
      $canonicalURL: String
      $limit: Float
      $customPeers: [String!]
    ) {
      Company(canonicalURL: $canonicalURL) {
        ...CompanyFields
        peers(limit: $limit, customPeers: $customPeers) {
          companyId
          score
          company {
            ...CompanyFields
          }
        }
      }
    }
  `,
  [COMPANY_FIELDS_FRAGMENT]
);

type Result = ResultOf<typeof QUERY>;

// TODO: This should be move to its own util file. Issue is that it relys on the query result type now
// Wasn't sure how best to refactor this as there's a bigger refactor needed across the peer work
export function getCustomPeersData(company: Result['Company']) {
  return company.peers.map((peer) => ({
    id: peer.company?.uniqueSymbol,
    name: peer.company?.name,
  }));
}

export type CustomPeers = ReturnType<typeof getCustomPeersData>;

export const queryPeersFn = async ({
  queryKey: [_keyof, canonicalURL, limit, customPeers],
}: {
  queryKey: ReturnType<typeof getPeersQueryKey>;
}) => {
  const response = await gqlClient.request(QUERY, {
    canonicalURL,
    limit,
    customPeers: customPeers.filter(Boolean),
  });

  return response;
};

// TODO: This should be move to its own file
export const usePeers = ({
  canonicalUrl,
  peers,
}: {
  canonicalUrl?: string;
  peers: string[];
}) =>
  useQuery({
    queryKey: getPeersQueryKey(canonicalUrl, PEERS_LIMIT, peers),
    queryFn: queryPeersFn,
    staleTime: Infinity,
    enabled: Boolean(canonicalUrl),
    refetchOnMount: false,
  });

// TODO: This should be move to its own file
export type PeersQueryClientData = NonNullable<
  ReturnType<typeof usePeers>['data']
>;

// TODO: Both Histogram fns should be move into their own file
export const queryHistogramOptions = (canonicalUrl: string | undefined) => {
  return {
    staleTime: Infinity,
    enabled: Boolean(canonicalUrl),
    retryDelay: 4000,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      const graphQlErrors: GraphQLError[] | undefined = error?.response?.errors;
      const unprocessable =
        graphQlErrors &&
        graphQlErrors.length &&
        graphQlErrors.some((err) => err.extensions?.code === '422');
      if (unprocessable || failureCount > 2) {
        return false;
      }
      return true;
    },
  };
};

export const queryHistogramFn = async ({
  queryKey: [_keyof, canonicalURL, multiple],
}: QueryFunctionContext<[string, string, string | undefined]>) => {
  const response = await gqlClient.request(
    gql(`
      query getCompanyHistogram($canonicalURL: String, $multiple: String) {
        Company(canonicalURL: $canonicalURL) {
          industryHistogram(preferredMultiple: $multiple) {
            bucketSize
            countryISO
            level
            maxRange
            median
            minRange
            preferredMultiple
            secondaryIndustryId
            region
            count
            buckets {
              count
              key
              from
              to
            }
          }
        }
      }
    `),
    {
      canonicalURL,
      multiple: multiple?.toLowerCase(),
    }
  );
  const { errors } = response;
  if (errors && errors.length > 0) {
    console.log(errors[0].message);
    throw new Error(errors[0].message);
  }
  return response;
};
